import { Box, Skeleton } from "@mui/material";
import { memo } from "react";
import { SavedSearchesDropdown } from "..";
import { useFilterContext } from "../../contexts/FilterProvider";
import useFilterOptions from "../../contexts/useFilterOptions";
import { DataSourceFilter, Filter, FilterResponse } from "../../models/filter";
import { SearchFilters, SearchResults } from "../../models/search";
import FilterSectionVirtual from "../FilterSectionVirtual";
import { ClearFiltersButton, DateRangeFilter } from "../filters";
import ApplyFiltersButton from "../filters/ApplyFiltersButton";
import { getFilter, updateFilter } from "../filters/FilterFunctions";
import useLocalFilterState from "../filters/useLocalFilterState";
import useUserContext from "../Authorization/useUserContext";
import NationalAccountTreeFilter from "../filters/NationalAccountTreeFilter";

const BOTTOM_BOX_HEIGHT = 40;

const getActiveFiltersByDataType = (
  filtersByDatatype: DataSourceFilter[] | undefined,
  filterState: SearchFilters | undefined,
): Filter[] => {
  const activeFilters: Filter[] = [];
  if (!filtersByDatatype || !filterState) return activeFilters;

  filtersByDatatype.forEach((filter) => {
    if (filterState.base_filters.data_source?.includes(filter.data_source_name)) {
      filter.data_source_filters.forEach((f) => {
        const existingItem = activeFilters.find((af) => af.column_name === f.column_name);
        if (existingItem && existingItem.filter_values) {
          existingItem.filter_values = [...new Set([...existingItem.filter_values, ...(f.filter_values || [])])];
        } else {
          activeFilters.push(f);
        }
      });
    }
  });
  return activeFilters;
};

interface SidebarFilterContentProps {
  disabled: boolean;
}

// Renders the filter controls (saved searches, date range, filter sections)
function SidebarFilterContent({ disabled }: SidebarFilterContentProps) {
  const user = useUserContext();
  const { data: filterOptions } = useFilterOptions();
  const { filterState, hasActiveFilters, applyFilters } = useFilterContext();
  const { transientFilterState, setTransientFilterState, hasUnappliedFilterChanges } = useLocalFilterState(filterState);

  const handleFilterChange = (filterKey: keyof SearchResults, value: string[]) => {
    if (!filterOptions) return;
    setTransientFilterState((local) => updateFilter(local, filterOptions, filterKey, value));
  };

  const applyLocalFilters = () => {
    applyFilters(transientFilterState);
  };

  const showNewNationalAccountFilter = user.selectedRoles?.includes("medical");

  const allFilters: Filter[] = [];
  if (filterOptions && transientFilterState) {
    const activeByDataType = getActiveFiltersByDataType(filterOptions.data_source_filters, transientFilterState);
    allFilters.push(...(filterOptions.common_filters ?? []));
    allFilters.push(...activeByDataType.sort((a, b) => (a.column_name < b.column_name ? -1 : 1)));
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", paddingRight: "0.5em" }}>
        {/* Saved Searches & Date Range */}
        <Box sx={disabled ? { "& *": { cursor: "not-allowed", "& *": { pointerEvents: "none" } } } : undefined}>
          <Box sx={{ mb: 1, ...(disabled && { opacity: 0.5 }) }}>
            <SavedSearchesDropdown />
          </Box>
          <Box sx={{ mb: 1 }}>{filterOptions ? <DateRangeFilter /> : <Skeleton height={40} variant="rounded" />}</Box>
        </Box>

        {/* Filter Sections */}
        {allFilters.length === 0 && !filterOptions && <Skeleton height={100} variant="rounded" sx={{ mb: 1 }} />}
        {allFilters.map((filter) => {
          if (showNewNationalAccountFilter && filter.column_name === "corp_account") {
            return (
              <NationalAccountTreeFilter
                disabled={disabled}
                key="Corp Account"
                filterOptions={filterOptions!}
                filterState={transientFilterState!}
                appliedFilterState={filterState!}
                handleFilterChange={handleFilterChange}
              />
            );
          }
          return (
            <FilterSectionVirtual
              key={filter.filter_name}
              filter={filter}
              appliedValues={getFilter(filterState!, filter.column_name)}
              selectedValues={getFilter(transientFilterState!, filter.column_name)}
              disabled={disabled}
              handleFilterChange={handleFilterChange}
            />
          );
        })}
      </Box>

      {/* Clear / Apply buttons — matches Explore FilterDrawer */}
      {!disabled && (hasActiveFilters || hasUnappliedFilterChanges) && (
        <Box sx={{ borderTop: "1px solid rgba(0, 0, 0, 0.12)", p: 1.5, display: "flex", gap: 1 }}>
          <ClearFiltersButton height={BOTTOM_BOX_HEIGHT} />
          <ApplyFiltersButton
            height={BOTTOM_BOX_HEIGHT}
            hasUnappliedFilterChanges={hasUnappliedFilterChanges}
            applyFilters={applyLocalFilters}
          />
        </Box>
      )}
    </Box>
  );
}

export default memo(SidebarFilterContent);
